import type { ChoicesT } from '@mentimeter/question-modules-types';
import { getPercentageGetter } from '@mentimeter/visualizations-react/utils/get-percentage-getter';
import { useTranslations } from 'next-intl';
import type { Choice } from '../../types';

interface ScreenReaderTableProps {
  choices: Choice[];
  results: ChoicesT['results'];
  shouldShowCorrect: boolean;
  shouldShowScorePercentage: false | { percentOf: number };
  shouldHideResults: boolean;
}

export const ScreenReaderTable = ({
  choices,
  results,
  shouldShowCorrect,
  shouldShowScorePercentage,
  shouldHideResults,
}: ScreenReaderTableProps) => {
  const translate = useTranslations('presenting.screenreader_table');

  if (shouldHideResults) {
    return <span>{translate('results_are_hidden_message')}</span>;
  }

  const getScoreDisplayText = (score: number): string => {
    if (shouldShowScorePercentage) {
      const getPercentage = getPercentageGetter(
        shouldShowScorePercentage?.percentOf,
      );
      return getPercentage(score).toFixed(0) + '%';
    } else {
      return score.toString();
    }
  };

  return (
    <table>
      <caption>{translate('results_table_caption')}</caption>
      <thead>
        <tr>
          <th scope="col">{translate('option_column_title')}</th>
          <th scope="col">{translate('score_column_title')}</th>
          {shouldShowCorrect && <th>{translate('is_correct_column_title')}</th>}
        </tr>
      </thead>
      <tbody>
        {results.map((result) => {
          const resultScore = result.score.reduce(
            (acc, score) => acc + score,
            0,
          );

          const choice = choices.find((choice) => choice.id === result.id);
          return (
            <tr key={result.id}>
              <th scope="row">{result.label}</th>
              <td>{getScoreDisplayText(resultScore)}</td>
              {shouldShowCorrect && (
                <td>
                  {choice?.isMarkedCorrect
                    ? translate('is_option_correct_label')
                    : translate('is_option_not_correct_label')}
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};
